/* Multi-select on Nimstrata collection/search cards + one "Add all to cart" bar.
   Prototype on the "Ryan Test" theme (2026-10-20); spec in Foundation
   docs/superpowers/specs/2026-10-20-light-mode-and-bulk-add-design.md.

   The cards are the app's own markup (article.rcc-search__product). A MutationObserver
   decorates each one with a checkbox the same way rcc-product-tags decorates them with
   pills, so the app re-rendering the grid (filter, page, sort) just gets decorated again.
   The variant id comes from the card link's ?variant= param, which the app puts on
   single-variant products only; multi-variant cards get no box. The selection lives in
   sessionStorage per page, so paging and filtering keep it and a new tab starts clean.
   Adding is one /cart/add.js call per card: Shopify rejects a whole multi-item call when
   one line is sold out, and on Daily Singles a card selling between tick and click is the
   normal failure, so each card succeeds or fails on its own. Afterwards the theme's own
   cart-count refresh (on:cart:change) and added-notification run, as after any add. */
(function (root) {
  'use strict';

  // ponytail: 50 is a UX cap, not a technical one; raise if anyone hits it.
  var MAX_SELECTED = 50;

  // "/products/handle?variant=123" -> "123"; a link without a variant -> null.
  function variantIdFromHref(href) {
    var m = /[?&]variant=(\d+)/.exec(String(href || ''));
    return m ? m[1] : null;
  }

  // What the notification title and the bar note say after an add run.
  function summarize(added, failed) {
    var title = added === 1 ? '1 item' : added + ' items'; // Ryan 2026-10-21: "items", not "cards" (sealed too)
    var note = '';
    if (failed === 1) note = '1 item could not be added (already sold)';
    else if (failed > 1) note = failed + ' items could not be added (already sold)';
    return { title: title, note: note };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { variantIdFromHref: variantIdFromHref, summarize: summarize, MAX_SELECTED: MAX_SELECTED };
    return;
  }

  var doc = root.document;
  if (!doc || !root.MutationObserver) return;

  var KEY = 'rcc-bulk:' + root.location.pathname;
  var selected = load();
  var busy = false;
  var bar, countEl, addBtn, noteEl;

  function load() {
    try {
      var raw = root.sessionStorage.getItem(KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(arr) ? arr.map(String) : []);
    } catch (e) { return new Set(); }
  }

  function save() {
    try { root.sessionStorage.setItem(KEY, JSON.stringify(Array.from(selected))); } catch (e) { /* private mode: selection lives for the page only */ }
  }

  // --- the bar -------------------------------------------------------------------------

  function ensureBar() {
    if (bar) return bar;
    bar = doc.createElement('div');
    bar.className = 'rcc-bulk-bar';
    bar.hidden = true;
    bar.innerHTML =
      '<span class="rcc-bulk-bar__count"></span>' +
      '<button type="button" class="rcc-bulk-bar__add">Add to cart</button>' +
      '<button type="button" class="rcc-bulk-bar__clear">Clear</button>' +
      '<span class="rcc-bulk-bar__note" aria-live="polite"></span>';
    doc.body.appendChild(bar);
    countEl = bar.querySelector('.rcc-bulk-bar__count');
    addBtn = bar.querySelector('.rcc-bulk-bar__add');
    noteEl = bar.querySelector('.rcc-bulk-bar__note');
    addBtn.addEventListener('click', addAll);
    bar.querySelector('.rcc-bulk-bar__clear').addEventListener('click', clearAll);
    return bar;
  }

  function renderBar() {
    ensureBar();
    var n = selected.size;
    bar.hidden = n === 0;
    bar.classList.toggle('has-preview-bar', !!doc.querySelector('#PBarNextFrameWrapper, #preview-bar-iframe'));
    countEl.textContent = n === 1 ? '1 item selected' : n + ' items selected';
    if (!busy) addBtn.textContent = 'Add to cart'; // the count already reads "N items selected" (Ryan 2026-10-21)
  }

  function note(text) {
    ensureBar();
    noteEl.textContent = text || '';
  }

  // --- the checkboxes ------------------------------------------------------------------

  function syncCards(id) {
    doc.querySelectorAll('.rcc-bulk__check[data-variant-id="' + id + '"]').forEach(function (label) {
      var on = selected.has(id);
      label.querySelector('.rcc-bulk__input').checked = on;
      var card = label.closest('.rcc-search__product');
      if (card) {
        card.classList.toggle('is-bulk-selected', on);
        if (!on) card.classList.remove('is-bulk-failed');
      }
    });
  }

  function toggle(id, on) {
    if (on && selected.size >= MAX_SELECTED) {
      note('Up to ' + MAX_SELECTED + ' items at a time. Add these first, then pick more.');
      return;
    }
    if (on) selected.add(id); else selected.delete(id);
    note('');
    save();
    syncCards(id);
    renderBar();
  }

  function clearAll() {
    var ids = Array.from(selected);
    selected.clear();
    save();
    ids.forEach(syncCards);
    note('');
    renderBar();
  }

  function decorate(card) {
    if (card.querySelector('.rcc-bulk__check')) return;
    var link = card.querySelector('a[href*="/products/"]');
    var id = link ? variantIdFromHref(link.getAttribute('href')) : null;
    if (!id) return; // multi-variant (or no variant yet): no box
    var label = doc.createElement('label');
    label.className = 'rcc-bulk__check';
    label.dataset.variantId = id;
    label.innerHTML = '<input type="checkbox" class="rcc-bulk__input" aria-label="Select item for add all to cart"><span class="rcc-bulk__box" aria-hidden="true"></span>';
    var input = label.querySelector('.rcc-bulk__input');
    input.checked = selected.has(id);
    card.classList.toggle('is-bulk-selected', input.checked);
    // The card's own click handlers open the product; the box must not.
    label.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      toggle(id, !selected.has(id));
    });
    card.appendChild(label); // position is CSS only: the frame's top-right corner on every card (Ryan 2026-10-21)
  }

  function scan() {
    doc.querySelectorAll('article.rcc-search__product').forEach(decorate);
  }

  // --- adding --------------------------------------------------------------------------

  function addOne(id) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: Number(id), quantity: 1 })
    }).then(function (r) { return r.ok; }, function () { return false; });
  }

  function notify(title) {
    var theme = root.theme || {};
    var settings = theme.settings || {};
    if (settings.afterAddToCart === 'page' && theme.routes && theme.routes.cart) {
      root.location.href = theme.routes.cart;
      return;
    }
    var template = doc.getElementById('AddedNotification');
    if (!template) return;
    var el = template.content.firstElementChild.cloneNode(true);
    el.dataset.productTitle = title;
    (doc.querySelector('.pageheader--sticky') || doc.body).appendChild(el);
  }

  async function addAll() {
    if (busy || selected.size === 0) return;
    busy = true;
    addBtn.disabled = true;
    addBtn.textContent = 'Adding…';
    note('');
    var ids = Array.from(selected);
    var added = 0;
    var failed = [];
    for (var i = 0; i < ids.length; i++) {
      // sequential on purpose: the cart is one resource and Shopify throttles parallel writes
      var ok = await addOne(ids[i]); // eslint-disable-line no-await-in-loop
      if (ok) { added++; selected.delete(ids[i]); } else failed.push(ids[i]);
    }
    save();
    ids.forEach(syncCards);
    failed.forEach(function (id) {
      doc.querySelectorAll('.rcc-bulk__check[data-variant-id="' + id + '"]').forEach(function (label) {
        var card = label.closest('.rcc-search__product');
        if (card) card.classList.add('is-bulk-failed');
      });
    });
    busy = false;
    addBtn.disabled = false;
    renderBar();
    var summary = summarize(added, failed.length);
    note(summary.note);
    if (added) {
      doc.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
      notify(summary.title);
    }
  }

  // --- boot ----------------------------------------------------------------------------

  var pending = false;
  new MutationObserver(function () {
    if (pending) return;
    pending = true;
    root.requestAnimationFrame(function () { pending = false; scan(); });
  }).observe(doc.documentElement, { childList: true, subtree: true });

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', function () { scan(); renderBar(); });
  else { scan(); renderBar(); }
})(typeof window !== 'undefined' ? window : this);
